'use client';
import { Box, BoxProps, keyframes } from '@mui/material';
import { usePathname } from 'next/navigation';
import { useLayoutEffect, useRef } from 'react';
import { MOTION } from '../../constants';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: none; }
`;

/**
 * Wraps a route's content so it enters like a customer-mobile screen. Meant for `template.tsx`. A template only
 * remounts when its own child segment changes, so the entrance also replays on every other path change (tabs, nested
 * pages) without remounting the subtree. Pure CSS, so the page still renders on the server. The theme's
 * reduced-motion rule turns it off.
 *
 * @param {'fadeUp' | 'fade'} variant - Fade while sliding up (default), or fade only.
 */
export const PageTransition = ({ variant = 'fadeUp', sx, ...rest }: BoxProps & { variant?: 'fadeUp' | 'fade' }) => {
  const ref = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const shownPathname = useRef(pathname);

  // Restarts the entrance before the new page paints; the first mount already plays it
  useLayoutEffect(() => {
    if (shownPathname.current === pathname) return;
    shownPathname.current = pathname;
    ref.current?.getAnimations?.().forEach((animation) => {
      animation.cancel();
      animation.play();
    });
  }, [pathname]);

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        animation: `${variant === 'fade' ? fadeIn : fadeInUp} ${MOTION.medium}ms ${MOTION.easeOut} backwards`,
        ...sx,
      }}
      {...rest}
    />
  );
};
